/* eslint-disable consistent-return */
import Actions from 'actions/Actions';
import mfaVerificationApi from 'services/api/authentication/mfaVerification';

/**
 * Validates MFA (phone or email)
 * @param {string} phone - Phone number to validate
 * @param {string} email - User's email
 * @param {string} mfaMode - 'phone' or 'email'
 * @returns {Promise} - Promise that resolves with response or rejects with error
 */
function validateMFAEmailPhone(phone, email, mfaMode) {
    return async dispatch => {
        dispatch(Actions.showInterstice(true));

        try {
            const response = await mfaVerificationApi.validateMFAEmailPhone(phone, email, mfaMode);

            return response;
        } catch (error) {
            return Promise.reject(error);
        } finally {
            dispatch(Actions.showInterstice(false));
        }
    };
}

/**
 * Validates MFA (phone or email)
 * @param {string} email - User's email
 * @param {string} otpCode - OTP Code to validate
 * @param {string} mfaMode - 'phone' or 'email'
 * @param {string} keepSignedIn - 'true' or 'false'
 * @param {Object} [options] - New /signin flow (SIM-10) enhancements passed through to the API
 *   ({ securityToken, requestOrigin }); omitted by legacy MFA callers.
 * @param {boolean} [suppressInterstice] - when true, do NOT toggle the GLOBAL white
 *   interstice. The new /signin OTP flow shows its own dark-scrim circular loader
 *   (SignIn.f.jsx, FIX 3); toggling the interstice here too would stack BOTH loaders.
 *   Legacy MFA callers omit this, so their interstice behaviour is unchanged.
 * @returns {Promise} - Promise that resolves with response or rejects with error
 */
function validateOTP(email, otpCode, mfaMode, keepSignedIn, options = {}, suppressInterstice = false) {
    return async dispatch => {
        if (!suppressInterstice) {
            dispatch(Actions.showInterstice(true));
        }

        try {
            const response = await mfaVerificationApi.mfaVerifyOTP(email, otpCode, mfaMode, keepSignedIn, options);

            return response;
        } catch (error) {
            return Promise.reject(error);
        } finally {
            if (!suppressInterstice) {
                dispatch(Actions.showInterstice(false));
            }
        }
    };
}

/**
 * Send (trigger) an OTP for the new /signin flow (SIM-7 / ACS-4131, ADR-SIM7-002).
 * Thin thunk over mfaVerification.mfaSendOtp. The new flow does NOT use the global
 * interstice: SignIn.f.jsx renders the shared dark-scrim Loader for OTP send/verify
 * transitions, so this thunk deliberately does not toggle the interstice itself.
 * @param {string} securityToken - Intermediate token from authenticationMethods.
 * @param {string} mfaMode - 'phone' | 'email'.
 * @returns {Promise} - Promise that resolves with response or rejects with error.
 */
function triggerOtp(securityToken, mfaMode) {
    return async () => {
        return mfaVerificationApi.mfaSendOtp(securityToken, mfaMode);
    };
}

export default {
    validateMFAEmailPhone,
    validateOTP,
    triggerOtp
};
